import { gql, useLazyQuery } from '@apollo/client'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HelpIcon from '@mui/icons-material/Help'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import XIcon from '@mui/icons-material/X'
import {
  Avatar,
  ButtonBase,
  IconButton,
  ListItemText,
  styled,
  TableContainer,
} from '@mui/material'
import AvatarGroup from '@mui/material/AvatarGroup'
import Box from '@mui/material/Box'
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import {
  Retailer,
  Query,
  RetailerAccount,
} from '@promoboxx/graphql-gateway-types'
import I18n from 'i18n-js'
import { useEffect } from 'react'
import { makeStyles } from 'tss-react/mui'

import BottomAppBarSpacer from '@src/lib/BottomAppBarSpacer'
import ChildSpacer from '@src/lib/ChildSpacer'
import normalizeLocaleName from '@src/lib/normalizeLocaleName'
import useIsMobileBreakpoint from '@src/lib/useIsMobileBreakpoint'

import cloudinary from '../../lib/Cloudinary'
import LegacySvg from '../shared/LegacySvg'
import SmartImage from '../shared/SmartImage'
import {
  checkRetailerCustomFields,
  checkRetailerLocations,
} from '../taskList/taskListChecks'

import manageBrandsPaths from './manageBrandsPaths'
import useManageBrandDialog from './useManageBrandDialog'
const useStyles = makeStyles()((theme) => ({
  image: {
    width: 150,
    height: 75,
    objectFit: 'contain',
    [theme.breakpoints.only('xs')]: {
      width: 100,
      height: 50,
      objectFit: 'contain',
    },
  },
  pointer: {
    cursor: 'pointer',
  },
}))

const GET_RETAILER_ACCOUNT = gql`
  query getRetailerAccountForManageBrands($id: ID!, $fields: [String!]) {
    getRetailerAccount(id: $id, fields: $fields) {
      id
      locations {
        id
      }
      retailers {
        id
        created_at
        brand {
          id
          name
          logo_url
        }
        customFieldValues {
          id
          custom_field {
            id
            required
            retailer_permission
          }
          value
        }
        facebook_pages {
          id
          instagram_actor_id
          business_manager_permissions
          paid_ads_permissions_enabled
        }
        twitterAccounts {
          id
          has_been_authenticated
        }
        location_count
      }
    }
  }
`

const ManageBrands = () => {
  const params = manageBrandsPaths.index.useParams()
  const [getRetailerAccount, { data, loading, refetch }] =
    useLazyQuery<Query>(GET_RETAILER_ACCOUNT)
  const isMobile = useIsMobileBreakpoint()

  useEffect(() => {
    getRetailerAccount({
      variables: {
        id: params.retailerAccountSlug,
        fields: ['retailers', 'retailers__brand', 'retailers__facebook_pages'],
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.retailerAccountSlug])

  const { showManageBrandDialog } = useManageBrandDialog({
    onClose: () => refetch?.(),
  })

  const rows = data?.getRetailerAccount?.retailers || []

  return (
    <>
      {loading ? (
        <Paper>
          <LinearProgress />
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {isMobile ? (
                  <>
                    <TableCell align="center" style={{ width: 40 }}>
                      {I18n.t('shared.brand')}
                    </TableCell>
                    <TableCell align="center" style={{ width: 100 }}>
                      <ColumnTitleWithTooltip
                        tooltip={I18n.t(
                          'manageBrands.tooltips.availableChannels',
                        )}
                      >
                        {I18n.t('shared.channels')}
                      </ColumnTitleWithTooltip>
                    </TableCell>
                    <TableCell style={{ width: 20 }} />
                  </>
                ) : (
                  <>
                    <TableCell align="center" style={{ width: 40 }}>
                      {I18n.t('shared.brand')}
                    </TableCell>
                    <TableCell style={{ width: 40 }}>
                      {I18n.t('manageBrands.activeSince')}
                    </TableCell>
                    <TableCell align="center" style={{ width: 100 }}>
                      <ColumnTitleWithTooltip
                        tooltip={I18n.t(
                          'manageBrands.tooltips.availableChannels',
                        )}
                      >
                        {I18n.t('manageBrands.availableChannels')}
                      </ColumnTitleWithTooltip>
                    </TableCell>
                    <TableCell align="center" style={{ width: 60 }}>
                      <ColumnTitleWithTooltip
                        tooltip={I18n.t(
                          'manageBrands.tooltips.activatedPaidAds',
                        )}
                      >
                        {I18n.t('manageBrands.activatedPaidAds')}
                      </ColumnTitleWithTooltip>
                    </TableCell>
                    <TableCell align="center" style={{ width: 60 }}>
                      <ColumnTitleWithTooltip
                        tooltip={I18n.t('manageBrands.tooltips.locations')}
                      >
                        {I18n.t('manageBrands.locations')}
                      </ColumnTitleWithTooltip>
                    </TableCell>
                    <TableCell align="center" style={{ width: 40 }}>
                      <ColumnTitleWithTooltip
                        tooltip={I18n.t('manageBrands.tooltips.brandQuestions')}
                      >
                        {I18n.t('manageBrands.brandQuestions')}
                      </ColumnTitleWithTooltip>
                    </TableCell>
                    <TableCell style={{ width: 20 }} />
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((retailer) => (
                <BrandRow
                  key={retailer.id}
                  retailer={retailer}
                  retailerAccount={data?.getRetailerAccount}
                  onClickSettings={() => {
                    showManageBrandDialog({
                      retailerId: retailer.id,
                    })
                  }}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <BottomAppBarSpacer />
    </>
  )
}

// Copilot did this!
const formatDate = (date: string) => {
  const dateObject = new Date(date)
  const locale = normalizeLocaleName(I18n.locale)
  const options: Intl.DateTimeFormatOptions | undefined = {
    year: 'numeric',
    month: 'long',
  }
  return new Intl.DateTimeFormat(locale, options).format(dateObject)
}

const tooltipStyles = makeStyles()((theme) => ({
  tooltip: {
    marginLeft: theme.spacing(1),
  },
}))

interface ColumnTitleWithTooltipProps {
  tooltip: string
}
const ColumnTitleWithTooltip: React.FC<
  React.PropsWithChildren<ColumnTitleWithTooltipProps>
> = ({ children, tooltip }) => {
  const { classes } = tooltipStyles()
  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      <Box>{children}</Box>
      <Tooltip title={tooltip} placement="top">
        <HelpIcon
          fontSize="small"
          color="disabled"
          className={classes.tooltip}
        />
      </Tooltip>
    </Box>
  )
}

interface BrandRowProps {
  retailer: Retailer
  retailerAccount?: RetailerAccount | null
  onClickSettings: () => void
}
const BrandRow: React.FC<React.PropsWithChildren<BrandRowProps>> = ({
  retailer,
  retailerAccount,
  onClickSettings,
}) => {
  const { classes } = useStyles()
  const isMobile = useIsMobileBreakpoint()

  const locationsCheck = checkRetailerLocations(retailer, retailerAccount)
  const customFieldsCheck = checkRetailerCustomFields(retailer)

  return (
    <TableRow>
      {isMobile ? (
        <>
          <TableCell align="center" className={classes.pointer}>
            <ButtonBase
              onClick={onClickSettings}
              style={{ backgroundColor: 'transparent' }}
            >
              <div>
                <SmartImage
                  alt={retailer.brand?.name || ''}
                  className={classes.image}
                  fallbackSrc={cloudinary
                    .instance()
                    .url('preview-defaults/campaign-default')}
                  src={retailer.brand?.logo_url}
                />
                <Box p={2} textAlign="center">
                  <Typography>{retailer.brand?.name}</Typography>
                </Box>
              </div>
            </ButtonBase>
          </TableCell>
          <TableCell align="center">
            <ChannelGroup retailer={retailer} />
          </TableCell>
          <TableCell align="right">
            <IconButton onClick={onClickSettings} size="large">
              <SettingsOutlinedIcon />
            </IconButton>
          </TableCell>
        </>
      ) : (
        <>
          <TableCell className={classes.pointer}>
            <ButtonBase
              onClick={onClickSettings}
              style={{ backgroundColor: 'transparent', width: '100%' }}
            >
              <Box display="flex" alignItems="center" width="100%" py={1}>
                <ChildSpacer>
                  <SmartImage
                    alt={retailer.brand?.name || ''}
                    className={classes.image}
                    fallbackSrc={cloudinary
                      .instance()
                      .url('preview-defaults/campaign-default')}
                    src={retailer.brand?.logo_url}
                  />
                  <ListItemText primary={retailer.brand?.name} />
                </ChildSpacer>
              </Box>
            </ButtonBase>
          </TableCell>
          <TableCell>{formatDate(retailer.created_at!)}</TableCell>
          <TableCell align="center">
            <ChannelGroup retailer={retailer} />
          </TableCell>
          <TableCell align="center">
            {authenticatedRetailer(retailer) ? (
              <CheckCircleOutlineIcon color="secondary" />
            ) : (
              <ErrorOutlineIcon color="disabled" />
            )}
          </TableCell>
          <TableCell align="center">
            <ProgressBar
              value={locationsCheck.numConnected}
              total={locationsCheck.numTotal}
            />
          </TableCell>
          <TableCell>
            <ProgressBar
              value={customFieldsCheck.numAnswered}
              total={customFieldsCheck.numTotal}
            />
          </TableCell>
          <TableCell align="right">
            <IconButton onClick={onClickSettings} size="large">
              <SettingsOutlinedIcon />
            </IconButton>
          </TableCell>
        </>
      )}
    </TableRow>
  )
}

const authenticatedRetailer = (retailer: Retailer) => {
  if (!retailer?.facebook_pages?.length) {
    return false
  }
  return (retailer?.facebook_pages || []).some(
    (page) => page?.business_manager_permissions === true,
  )
}

interface ProgressBarProps {
  value: number
  total: number
}
const ProgressBar: React.FC<React.PropsWithChildren<ProgressBarProps>> = ({
  value,
  total,
}) => {
  if (value === total) {
    return (
      <Box textAlign="center">
        <CheckCircleOutlineIcon color="secondary" />
      </Box>
    )
  } else {
    return (
      <>
        <Progress
          variant="determinate"
          value={(value / total) * 100}
          color="info"
        />
        <Box mt={2} textAlign="center">
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: I18n.t('manageBrands.progress', { value, total }),
            }}
          />
        </Box>
      </>
    )
  }
}

const channelStyles = makeStyles()((theme) => ({
  legacySvg: {},
  platformAvatar: {
    background: theme.palette.background.paper,
    borderColor: theme.palette.background.paper,
    borderRadius: '50%',
    width: 24,
    height: 24,
  },
}))

type SocialChannels = 'facebook' | 'twitter' | 'instagram'
const activeChannelRules: Record<
  SocialChannels,
  (retailer: Retailer) => Boolean
> = {
  facebook: (retailer) => (retailer?.facebook_pages || []).length > 0,
  twitter: (retailer) =>
    (retailer?.twitterAccounts || []).some(
      (account) => account?.has_been_authenticated,
    ),
  instagram: (retailer) =>
    (retailer?.facebook_pages || []).some((page) => page?.instagram_actor_id),
}

interface ChannelGroupProps {
  retailer: Retailer
}

const ChannelGroup: React.FC<React.PropsWithChildren<ChannelGroupProps>> = ({
  retailer,
}) => {
  const { classes } = channelStyles()
  return (
    <Box display="flex" justifyContent="center">
      <AvatarGroup max={100}>
        {Object.keys(activeChannelRules).map((channel) => {
          const active = activeChannelRules[channel as SocialChannels](retailer)

          return (
            <Avatar className={classes.platformAvatar} key={channel}>
              {channel === 'twitter' ? (
                <XIcon
                  sx={{
                    backgroundColor: '#000',
                    width: '24px',
                    height: '24px',
                    filter: active ? 'none' : 'grayscale(100%)',
                    opacity: active ? 1 : 0.15,
                  }}
                />
              ) : (
                <div
                  style={
                    active ? {} : { filter: 'grayscale(100%)', opacity: 0.15 }
                  }
                >
                  <LegacySvg height="24px" symbol={channel} width="24px" />
                </div>
              )}
            </Avatar>
          )
        })}
      </AvatarGroup>
    </Box>
  )
}

const Progress = styled(LinearProgress)(() => ({
  height: 10,
  borderRadius: 9999,
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 9999,
  },
}))

export default ManageBrands
